import { Dispatch } from '@reduxjs/toolkit';
import {
  setAllAgentsApiCalling,
  setAllAgentsData,
  setAllAgentsFilters
} from '@cp/src/reducers/TeamLeadSlice';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { queryParam, filterQueryParams } from './interface';
import { CreateAgentRequest, UpdateAgentRequest } from './AgentForm/interfaces/types';
import { getFileDownload } from '@cp/src/utils/commonUtils';

export const getAllAgents =
  (queryParams: queryParam, filterQueryParams?: filterQueryParams) => (dispatch: Dispatch) => {
    dispatch(setAllAgentsApiCalling());
    const url = getApiUrl(ApiKeys.GET_ALL_AGENTS);
    const params = { ...queryParams };
    delete params.query;

    axiosInstance
      .post(
        url,
        {
          query: queryParams.query,
          filters: filterQueryParams
        },
        { params, headers: { includeInUptimeMetrics: true } }
      )
      .then(response => {
        dispatch(setAllAgentsData(response.data));
        dispatch(setAllAgentsApiCalling());
      })
      .catch(error => {
        dispatch(setAllAgentsApiCalling());
        logError('Error inside getAllAgentsAPI', error);
      });
  };

export const getAllAgentsFilters = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_ALL_AGENTS_FILTERS);
  axiosInstance
    .get(url)
    .then(response => {
      dispatch(setAllAgentsFilters(response.data));
    })
    .catch(error => {
      logError('Error inside getAllAgentsFilters', error);
    });
};

export const getAgentById = async (referenceId: string) => {
  try {
    const url = getApiUrl(ApiKeys.UPDATE_AGENT_DETAILS, { referenceId: referenceId });
    const response = await axiosInstance.get(url, { headers: { includeInUptimeMetrics: true } });
    return response.data;
  } catch (error) {
    logError(error, 'Error inside getAgentById');
  }
};

export const createAgent = (
  request: CreateAgentRequest,
  setIsSubmitButtonDisabled: (value: boolean) => void,
  setShowLoader: (value: boolean) => void,
  onClose: () => void
) => {
  setShowLoader(true);
  setIsSubmitButtonDisabled(true);
  const newRequest: CreateAgentRequest = { ...request };
  let keys: keyof typeof request;
  for (keys in request) {
    if (typeof request[keys] === 'boolean' || Boolean(request[keys])) {
      newRequest[keys] = request[keys] as any;
      continue;
    }
    newRequest[keys] = null;
  }
  newRequest.reportingManagerReferenceId = request.reportingManagerReferenceId;

  const toastMessage = request.name + ' added successfully';

  const url = getApiUrl(ApiKeys.CREATE_AGENT_DETAILS);

  axiosInstance
    .post(url, newRequest, {
      headers: {
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(response => {
      toast(toastMessage, { type: 'success' });
      setShowLoader(false);
      onClose();
    })
    .catch(error => {
      toast(error?.response?.data?.message || 'Agent creation failed', { type: 'error' });
      setShowLoader(false);
    })
    .finally(() => {
      setIsSubmitButtonDisabled(false);
    });
};

export const updateAgent = (
  referenceId: string,
  request: UpdateAgentRequest,
  setIsSubmitButtonDisabled: (value: boolean) => void,
  setShowLoader: (value: boolean) => void,
  onClose: () => void,
  successCallback: () => void
) => {
  const newRequest: Record<string, unknown> = { ...request };
  setShowLoader(true);
  setIsSubmitButtonDisabled(true);
  let keys: keyof typeof request;
  for (keys in request) {
    if (typeof request[keys] === 'boolean' || Boolean(request[keys])) {
      newRequest[keys] = request[keys] as string;
      continue;
    }
    newRequest[keys] = null;
  }
  newRequest.reportingManagerReferenceId = request.reportingManagerReferenceId;

  const url = getApiUrl(ApiKeys.UPDATE_AGENT_DETAILS, { referenceId: referenceId });
  const toastMessage = 'Details for ' + request.name + ' updated successfully';

  axiosInstance
    .put(url, newRequest, {
      headers: {
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(response => {
      toast(toastMessage, { type: 'success' });
      setShowLoader(false);
      successCallback();
      onClose();
    })
    .catch(error => {
      if (error?.response?.data?.status_code === 422) {
        toast(error?.response?.data?.message, { type: 'error' });
      } else toast('Agent update failed', { type: 'error' });
      setShowLoader(false);
    })
    .finally(() => {
      setIsSubmitButtonDisabled(false);
    });
};

export const uploadAgentsFile = (file: File, selectedRole: string) => () => {
  const url = getApiUrl(ApiKeys.UPLOAD_AGENTS, { selectedRole });
  const formData = new FormData();
  formData.append('file', file);
  axiosInstance
    .post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      toast.success('Successfully added agents.');
    })
    .catch(error => {
      toast.error(error || 'Failed to add agents.');
    });
};

export const uploadEditAgentsFile = (file: File) => () => {
  const url = getApiUrl(ApiKeys.UPLOAD_EDIT_AGENTS);
  const formData = new FormData();
  formData.append('file', file);
  axiosInstance
    .post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      toast.success('Successfully updated agents.');
    })
    .catch(error => {
      toast.error(error || 'Failed to update agents.');
    });
};

export const downloadSampleFile = (flow: string) => {
  const url = getApiUrl(ApiKeys.DOWNLOAD_BULK_SAMPLE_FILE);
  return (dispatch: Dispatch<any>) => {
    axiosInstance.get(url, { params: { flow } }).then(response => {
      if (response?.data) {
        const fileUrl = response?.data;
        getFileDownload(fileUrl);
      }
    });
  };
};
